import React from 'react';

/**
 * Generic React error boundary.
 *
 * Catches render/runtime errors anywhere in its subtree and shows a fallback
 * instead of letting the whole React tree unmount to a blank white screen
 * (React's default for an uncaught error). Used both globally (around the app's
 * routes) and scoped around the Spline 3D hero, which can throw in production
 * when the remote WebGL scene fails to load.
 *
 * Pass `fallback` to control what renders on error (e.g. an empty placeholder so
 * a failed hero just disappears). With no `fallback`, a friendly reload card is
 * shown instead of a white page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div
          style={{
            minHeight: '60vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '2rem',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#be185d' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '1.25rem' }}>
            This page hit an unexpected error. Please reload.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#db2777',
              color: '#fff',
              border: 'none',
              borderRadius: '9999px',
              padding: '0.6rem 1.4rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
